import React, { useEffect, useState } from 'react';
import t from '../../i18n/intl';
import { LoadingButton } from '@atlaskit/button';
import { useDispatch, useSelector } from 'react-redux';
import { State } from '../../../model/State';
import { AddressLine1, AddressLine2, City, CompanyName, PostCode } from './ContactFields';
import { styled } from 'styled-components';
import { Dimensions } from '../../../components/visuals';
import Form, { FormHeader } from '@atlaskit/form';
import { StateProvince } from './ContactStateSelect';
import { Country } from './ContactCountrySelect';
import { Country as CountryType } from 'modules/data/countries/model';
import { actions as countryActions } from '../../data/countries';
import { ContactTaxIdI as TaxId } from './ContactTaxId';
import * as taxRates from '../../data/taxRates';
import config from '../../../appconfig';
import { FormattedMessage } from 'react-intl';
import { fetchExternal, fetchValidateCountryChange } from '../duck/actions/contact';
import { selectValidateCountryChange } from '../duck/selectors';
import { postReconcilableValidationResultAccounts } from '../duck/actions/accountId';

const StyledSubmitButton = styled(LoadingButton)`
  margin-top: ${Dimensions.unit}px;
`;

export interface BillingOrgContactFormProps {
  displayName: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  address1: string | null;
  address2: string | null;
  city: string | null;
  country: string;
  expert: boolean;
  isValidForTaas: boolean;
  languagePreference: string;
  partner: boolean;
  postcode: string | null;
  state: string | null;
  taasValidatedOn: string | null;
  taasValidationErrorCode: string | null;
  taasValidationErrorReason: string | null;
  taxId: string | null;
  isRuByRestricted?: boolean;
  orgContacts?: any;
}

export const BillingOrgContactForm: React.FC<BillingOrgContactFormProps> = (
  props: BillingOrgContactFormProps,
) => {
  const dispatch = useDispatch();
  const countries = useSelector((s: State) => s.data.countries.all).ifSuccess((r) => r);
  const [selectedCountry, setSelectedCountry] = useState<CountryType | null>(null);
  const selectedCountryDetails =
    useSelector(
      (s: State) => s.data.countries.details[selectedCountry?.isoCountryCode || ''],
    )?.ifSuccess((r) => r) || null;

  const [selectedState, setSelectedState] = useState<{ name: string; code: string } | null>(null);
  const selectedTaxRate =
    useSelector(
      (s: State) =>
        s.data.taxRates[selectedCountry?.isoCountryCode || '']?.[selectedState?.code || ''],
    )?.ifSuccess((r) => r) || null;

  const [vatResponsible, setVatResponsible] = useState<boolean | null>(null);
  const selectValidateCountry = useSelector((state: State) =>
    selectValidateCountryChange(state, selectedCountry?.countryIsoCode || ''),
  );

  const handleSelectCountry = (country: CountryType | null) => {
    setSelectedCountry(country);
    dispatch(fetchValidateCountryChange(country?.isoCountryCode));
  };

  const handleSelectState = (state: { name: string; code: string } | null) => {
    setSelectedState(state);
  };

  const handleChangeVatResponsible = (isVatResponsible) => {
    setVatResponsible(isVatResponsible);
  };

  useEffect(() => {
    if (selectedCountry?.countryIsoCode) {
      dispatch(countryActions.fetchMissingDetails(selectedCountry?.countryIsoCode));
    }
  }, [selectedCountry?.countryIsoCode]);

  useEffect(() => {
    if (selectedCountry?.countryIsoCode && selectedState?.code) {
      dispatch(taxRates.actions.fetchTaxRate(selectedCountry?.countryIsoCode, selectedState?.code));
    }
  }, [selectedCountry?.countryIsoCode, selectedState?.code]);

  const showUpdateOrgDetailsWarning = props.orgContacts?.length > 1;
  const orgContactsToDisplay = props.orgContacts?.filter(
    (contact) => contact.contactDetails.email !== props.email,
  );

  return (
    <div style={{ marginTop: `${Dimensions.units(4)}px` }}>
      <Form
        onSubmit={(data) => {
          const orgDetails = {
            contactDetails: {
              firstName: props.firstName,
              lastName: props.lastName,
              email: props.email,
            },
            organisationDetails: {
              ...data,
              country: data.country?.isoCountryCode,
              state: selectedState?.code,
              payingVatResponsible: vatResponsible,
            },
          };
          if (selectValidateCountry) {
            dispatch(postReconcilableValidationResultAccounts(selectValidateCountry));
            dispatch(fetchExternal(orgDetails));
          }
        }}
      >
        {({ formProps, submitting }) => (
          <form {...formProps} data-cy="mac-billing-org-contact-form">
            <FormHeader title={t('mac.edit-details.org.contact.title')} />
            {showUpdateOrgDetailsWarning ? (
              <>
                <FormattedMessage
                  id="mac.edit-details.org.contact.warning"
                  values={{
                    contactUs: (
                      // eslint-disable-next-line react/jsx-no-target-blank
                      <a href={config.companyContact} target="_blank">
                        <FormattedMessage id="common.contact-us" />
                      </a>
                    ),
                  }}
                />
                <ul>
                  {orgContactsToDisplay.map((contact) => (
                    <li key={contact.contactDetails.displayName}>
                      {contact.contactDetails.displayName} {contact.contactDetails.email}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            <CompanyName {...props} countries={countries} />
            <AddressLine1 {...props} countries={countries} />
            <AddressLine2 {...props} countries={countries} />
            <City {...props} countries={countries} />
            <StateProvince
              state={props.state}
              countryDetails={selectedCountryDetails}
              onSelectState={handleSelectState}
            />
            <Country
              country={props.country}
              countries={countries || []}
              onSelectCountry={handleSelectCountry}
              hasIrreconcilableWarning={selectValidateCountry?.irreconcilable?.length > 0}
              hasReconcilableWarning={selectValidateCountry?.reconcilable?.length > 0}
            />
            <PostCode {...props} countries={countries} />
            <TaxId
              {...props}
              country={selectedCountryDetails}
              taxRate={selectedTaxRate}
              vatResponsible={vatResponsible}
              onChangeVatResponsible={handleChangeVatResponsible}
            />
            <StyledSubmitButton
              type="submit"
              appearance="primary"
              isLoading={submitting}
              isDisabled={submitting || props.isRuByRestricted}
              interactionName="checkout-contacts-submit"
              testId="mac-billing-org-details-submit-button"
            >
              {t('mac.edit-details.contact.submit')}
            </StyledSubmitButton>
          </form>
        )}
      </Form>
    </div>
  );
};
